import React, { useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import Input from './Input';
import Select from './Select';

const UF_OPTIONS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS',
  'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC',
  'SP', 'SE', 'TO',
].map(uf => ({ value: uf, label: uf }));

export interface AddressFormValues {
  cep: string;
  rua: string;
  numero: string;
  bairro: string;
  cidade: string;
  estado: string;
  complemento: string;
  apelido: string;
}

export const emptyAddressForm: AddressFormValues = {
  cep: '',
  rua: '',
  numero: '',
  bairro: '',
  cidade: '',
  estado: '',
  complemento: '',
  apelido: '',
};

interface StructuredAddressFormProps {
  values: AddressFormValues;
  onChange: (field: keyof AddressFormValues, value: string) => void;
  errors?: Partial<Record<keyof AddressFormValues, string>>;
  disabled?: boolean;
  showApelido?: boolean;
}

const StructuredAddressForm: React.FC<StructuredAddressFormProps> = ({
  values,
  onChange,
  errors = {},
  disabled = false,
  showApelido = true,
}) => {
  const [loadingCep, setLoadingCep] = useState(false);
  const [cepError, setCepError] = useState('');

  const handleCepSearch = async () => {
    const cepClean = values.cep.replace(/\D/g, '');
    if (cepClean.length !== 8) {
      setCepError('CEP deve ter 8 digitos');
      return;
    }

    setLoadingCep(true);
    setCepError('');

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cepClean}/json/`);
      const data = await response.json();

      if (data.erro) {
        setCepError('CEP nao encontrado');
        return;
      }

      if (data.logradouro) onChange('rua', data.logradouro);
      if (data.bairro) onChange('bairro', data.bairro);
      if (data.localidade) onChange('cidade', data.localidade);
      if (data.uf) onChange('estado', data.uf);
    } catch {
      setCepError('Erro ao buscar CEP');
    } finally {
      setLoadingCep(false);
    }
  };

  const formatCep = (value: string) => {
    const numbers = value.replace(/\D/g, '').slice(0, 8);
    if (numbers.length > 5) {
      return `${numbers.slice(0, 5)}-${numbers.slice(5)}`;
    }
    return numbers;
  };

  return (
    <div className="space-y-3">
      {/* CEP + Buscar */}
      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <Input
            label="CEP"
            value={values.cep}
            onChange={(e: any) => {
              onChange('cep', formatCep(e.target.value));
              setCepError('');
            }}
            placeholder="00000-000"
            disabled={disabled}
            error={cepError || errors.cep}
            maxLength={9}
          />
        </div>
        <button
          type="button"
          onClick={handleCepSearch}
          disabled={disabled || loadingCep || values.cep.replace(/\D/g, '').length < 8}
          className="btn btn-order-link text-[13px] px-4 py-[10px] flex items-center gap-1.5 whitespace-nowrap"
          style={{ height: '42px' }}
        >
          {loadingCep ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Search className="w-4 h-4" />
          )}
          Buscar
        </button>
      </div>

      {/* Rua */}
      <Input
        label="Rua *"
        value={values.rua}
        onChange={(e: any) => onChange('rua', e.target.value)}
        placeholder="Nome da rua"
        disabled={disabled}
        error={errors.rua}
      />

      {/* Numero + Complemento */}
      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Número *"
          value={values.numero}
          onChange={(e: any) => onChange('numero', e.target.value)}
          placeholder="123"
          disabled={disabled}
          error={errors.numero}
        />
        <Input
          label="Complemento"
          value={values.complemento}
          onChange={(e: any) => onChange('complemento', e.target.value)}
          placeholder="Apto, bloco..."
          disabled={disabled}
          error={errors.complemento}
        />
      </div>

      {/* Bairro */}
      <Input
        label="Bairro *"
        value={values.bairro}
        onChange={(e: any) => onChange('bairro', e.target.value)}
        placeholder="Nome do bairro"
        disabled={disabled}
        error={errors.bairro}
      />

      {/* Cidade + Estado */}
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <Input
            label="Cidade *"
            value={values.cidade}
            onChange={(e: any) => onChange('cidade', e.target.value)}
            placeholder="Nome da cidade"
            disabled={disabled}
            error={errors.cidade}
          />
        </div>
        <Select
          label="Estado *"
          value={values.estado}
          onChange={(value: any) => onChange('estado', value)}
          options={UF_OPTIONS}
          placeholder="UF"
          disabled={disabled}
          error={errors.estado}
        />
      </div>

      {/* Apelido */}
      {showApelido && (
        <Input
          label="Apelido (opcional)"
          value={values.apelido}
          onChange={(e: any) => onChange('apelido', e.target.value)}
          placeholder="Ex: Casa, Trabalho"
          disabled={disabled}
          error={errors.apelido}
        />
      )}
    </div>
  );
};

export default StructuredAddressForm;
